import { useEffect, useMemo, useState } from "react";

import { fetchPlatformRegisteredUsers, type PlatformRegisteredUserRow } from "../../../api/platformRegisteredUsers";
import { useTenants } from "../../../api/useTenants";

function fmtUtc(s: string): string {
  if (!s) return "—";
  try {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    return d.toLocaleString("zh-CN", { hour12: false });
  } catch {
    return s;
  }
}

function maskOpenId(openId: string): string {
  const s = (openId || "").trim();
  if (s.length <= 12) return s || "—";
  return `${s.slice(0, 6)}…${s.slice(-6)}`;
}

export default function PlatformRegisteredUsers() {
  const { tenants } = useTenants();
  const [rows, setRows] = useState<PlatformRegisteredUserRow[]>([]);
  const [selected, setSelected] = useState<PlatformRegisteredUserRow | null>(null);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [tenantLegacyId, setTenantLegacyId] = useState("");
  const [take, setTake] = useState(80);

  const tenantOptions = useMemo(
    () => [{ legacyId: "", name: "全部租户" }, ...tenants.map((t) => ({ legacyId: t.id, name: t.shortName || t.name }))],
    [tenants],
  );

  const load = async () => {
    setLoading(true);
    try {
      const data = await fetchPlatformRegisteredUsers({
        tenantLegacyId: tenantLegacyId || undefined,
        q: q.trim() || undefined,
        skip: 0,
        take,
      });
      setRows(data);
      setSelected((prev) => (prev ? data.find((x) => x.id === prev.id) || null : null));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantLegacyId, take]);

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-lg font-bold text-gray-800">注册用户</h2>
          <p className="text-xs text-gray-400 mt-0.5">
            平台级微信注册用户（OpenID 唯一）。按租户筛选时仅显示在该租户存在入会申请或在册会员关联的用户。
          </p>
        </div>
        <button
          onClick={() => void load()}
          className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white text-sm rounded-xl hover:bg-amber-400 cursor-pointer whitespace-nowrap"
        >
          <i className={loading ? "ri-loader-4-line animate-spin" : "ri-refresh-line"}></i>
          刷新
        </button>
      </div>

      <div className="bg-white rounded-2xl p-5 mb-4">
        <div className="grid grid-cols-12 gap-3 items-end">
          <div className="col-span-4">
            <label className="block text-xs font-medium text-gray-600 mb-1.5">关键字</label>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="昵称 / OpenID"
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400"
            />
          </div>
          <div className="col-span-4">
            <label className="block text-xs font-medium text-gray-600 mb-1.5">按租户关联筛选</label>
            <select
              value={tenantLegacyId}
              onChange={(e) => setTenantLegacyId(e.target.value)}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400"
            >
              {tenantOptions.map((t) => (
                <option key={t.legacyId || "all"} value={t.legacyId}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>
          <div className="col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1.5">显示条数</label>
            <select
              value={String(take)}
              onChange={(e) => setTake(parseInt(e.target.value, 10) || 80)}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400"
            >
              {[50, 80, 120, 200].map((n) => (
                <option key={n} value={String(n)}>
                  {n}
                </option>
              ))}
            </select>
          </div>
          <div className="col-span-2 flex gap-2 justify-end">
            <button
              onClick={() => void load()}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-gray-900 text-white text-sm rounded-xl hover:bg-gray-800 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-search-line"></i>
              查询
            </button>
          </div>
        </div>
      </div>

      <div className="flex gap-5">
        <div className="flex-1 min-w-0 space-y-3">
          {rows.length === 0 ? (
            <div className="bg-white rounded-2xl p-6 text-center text-sm text-gray-500">
              {loading ? "加载中…" : "暂无注册用户"}
            </div>
          ) : (
            rows.map((u) => {
              const isActive = selected?.id === u.id;
              return (
                <div
                  key={u.id}
                  onClick={() => setSelected(u)}
                  className={`bg-white rounded-2xl p-5 cursor-pointer transition-all border-2 ${
                    isActive ? "border-amber-400" : "border-transparent hover:border-gray-200"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gray-100 overflow-hidden flex items-center justify-center flex-shrink-0">
                      {u.avatarUrl ? (
                        <img src={u.avatarUrl} alt={u.nickName || "avatar"} className="w-full h-full object-cover" />
                      ) : (
                        <i className="ri-user-3-line text-gray-400"></i>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <h4 className="text-sm font-semibold text-gray-800 leading-snug truncate">
                            {u.nickName || "（未提供昵称）"}
                          </h4>
                          <p className="text-xs text-gray-400 mt-1">
                            OpenID：<span className="font-mono">{maskOpenId(u.wxOpenId)}</span>
                          </p>
                        </div>
                        <div className="flex-shrink-0 text-right">
                          <p className="text-[10px] text-gray-400 mt-1">最近活跃：{fmtUtc(u.lastSeenAtUtc)}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="w-96 flex-shrink-0">
          <div className="bg-white rounded-2xl p-5 sticky top-6 border border-gray-100">
            {!selected ? (
              <div className="text-sm text-gray-500">选择左侧用户查看详情</div>
            ) : (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-gray-800">用户详情</h3>
                  <button
                    onClick={() => setSelected(null)}
                    className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-xl hover:bg-gray-200 cursor-pointer"
                  >
                    <i className="ri-close-line text-gray-500"></i>
                  </button>
                </div>

                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-2xl bg-gray-100 overflow-hidden flex items-center justify-center flex-shrink-0">
                    {selected.avatarUrl ? (
                      <img src={selected.avatarUrl} alt={selected.nickName || "avatar"} className="w-full h-full object-cover" />
                    ) : (
                      <i className="ri-user-3-line text-gray-400 text-lg"></i>
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-gray-800 truncate">{selected.nickName || "（未提供昵称）"}</p>
                    <p className="text-xs text-gray-400 mt-0.5">平台注册用户</p>
                  </div>
                </div>

                <div className="space-y-3">
                  {[
                    { label: "用户ID", value: selected.id },
                    { label: "OpenID", value: selected.wxOpenId },
                    { label: "注册时间", value: fmtUtc(selected.createdAtUtc) },
                    { label: "最近活跃", value: fmtUtc(selected.lastSeenAtUtc) },
                  ].map((it) => (
                    <div key={it.label} className="p-3 bg-gray-50 rounded-xl">
                      <p className="text-xs text-gray-500 mb-1">{it.label}</p>
                      <p className="text-xs text-gray-800 break-all font-mono">{it.value || "—"}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

